import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  FilterList as FilterIcon
} from "@mui/icons-material";
import Swal from "sweetalert2";
import apiClient from "@/api/client";

const AdminAuditLog = () => {
  const [adminAudit, setAdminAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  
  // Pagination state - DataGrid will manage this internally
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      // Fetch all records (backend should handle pagination)
      const response = await apiClient.get('/admin/audit-logs', {
        params: { per_page: 10000 }
      });
      
      // Axios response: response.data
      const data = response.data;
      const logs = Array.isArray(data.data) ? data.data : 
                   Array.isArray(data) ? data : [];
      setAdminAudit(logs);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      if (error.response?.status === 401) {
        Swal.fire({
          icon: "error",
          title: "Unauthorized!",
          text: "Please login again to continue.",
          confirmButtonText: "OK",
        }).then(() => {
          localStorage.removeItem("token");
          window.location.href = "/login";
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.response?.data?.message || error.message || "Failed to fetch audit logs.",
          confirmButtonText: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Get unique action types for filter
  const actionTypes = useMemo(() => {
    const types = new Set(adminAudit.map(log => log.action_type));
    return Array.from(types);
  }, [adminAudit]);

  const getActionChip = (actionType) => {
    const colors = {
      form_submission: 'success',
      form_update: 'info',
      form_deletion: 'error',
      status_update: 'warning',
      user_login: 'primary',
      user_logout: 'secondary',
      permission_change: 'warning',
    };
    
    return (
      <Chip 
        label={actionType?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
        color={colors[actionType] || 'default'}
        size="small"
        sx={{ fontWeight: 500, minWidth: 100 }}
      />
    );
  };

  const getRoleChip = (role) => {
    return (
      <Chip 
        label={role}
        size="small"
        color={role === 'ADMIN' ? 'error' : 'primary'}
        variant="outlined"
        icon={role === 'ADMIN' ? <AdminIcon /> : undefined}
      />
    );
  };

  // Apply filters - client-side filtering
  const filteredRows = useMemo(() => {
    let filtered = adminAudit;

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(log => 
        log.user_name?.toLowerCase().includes(searchLower) ||
        log.user_email?.toLowerCase().includes(searchLower) ||
        log.action_type?.toLowerCase().includes(searchLower) ||
        log.entity_area?.toLowerCase().includes(searchLower) ||
        log.description?.toLowerCase().includes(searchLower) ||
        log.ip_address?.toLowerCase().includes(searchLower)
      );
    }

    // Apply action type filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action_type === actionFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(thisWeek.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(thisMonth.getMonth() - 1);

      filtered = filtered.filter(log => {
        const logDate = new Date(log.action_date_time);
        switch(dateFilter) {
          case 'today':
            return logDate >= today;
          case 'yesterday':
            return logDate >= yesterday && logDate < today;
          case 'thisWeek':
            return logDate >= thisWeek;
          case 'thisMonth':
            return logDate >= thisMonth;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [adminAudit, searchText, actionFilter, dateFilter]);

  const clearFilters = () => {
    setSearchText("");
    setActionFilter("all");
    setDateFilter("all");
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      sortable: true 
    },
    { 
      field: 'user_name', 
      headerName: 'Admin User', 
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32 }}>
            <AdminIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.user_email}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      field: 'user_role', 
      headerName: 'Role', 
      width: 120,
      sortable: true,
      renderCell: (params) => getRoleChip(params.value)
    },
    { 
      field: 'action_type', 
      headerName: 'Action', 
      width: 150,
      sortable: true,
      renderCell: (params) => getActionChip(params.value)
    },
    { 
      field: 'action_date_time', 
      headerName: 'Date & Time', 
      width: 200,
      sortable: true,
      valueGetter: (params) => new Date(params.value),
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    },
    { 
      field: 'entity_area', 
      headerName: 'Entity', 
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <Chip 
          label={params.value?.replace(/_/g, ' ') || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ 
            bgcolor: '#f0f0f0',
            fontWeight: 500
          }}
        />
      )
    },
    { 
      field: 'ip_address', 
      headerName: 'IP Address', 
      width: 130,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 350,
      sortable: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            py: 1
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                <SecurityIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="600" gutterBottom>
                  Admin Audit Log
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor all administrative actions and system changes
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAuditLogs}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by admin, email, action, entity..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <Button 
                      size="small" 
                      onClick={() => setSearchText('')}
                      sx={{ minWidth: 'auto' }}
                    >
                      <ClearIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                label="Action Type"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {actionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type?.replace(/_/g, ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date Range"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              startIcon={<FilterIcon />}
              sx={{ borderRadius: 2 }}
              disabled={!searchText && actionFilter === 'all' && dateFilter === 'all'}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="error.main" fontWeight="600">
              {adminAudit.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Actions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="primary.main" fontWeight="600">
              {actionTypes.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Action Types
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="success.main" fontWeight="600">
              {filteredRows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filtered Results
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="warning.main" fontWeight="600">
              {new Set(adminAudit.map(log => log.user_name)).size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Admins
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* DataGrid - Client-side pagination and sorting */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon color="action" />
              <Typography variant="h6" fontWeight="600">
                Audit Log Entries
              </Typography>
            </Stack>
            {searchText || actionFilter !== 'all' || dateFilter !== 'all' ? (
              <Chip 
                label={`${filteredRows.length} results found`}
                color="info"
                size="small"
                onDelete={clearFilters}
              />
            ) : (
              <Chip 
                label={`${adminAudit.length} total entries`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          <Divider />
          
          <Box sx={{ height: 'calc(100vh - 500px)', width: '100%', mt: 2 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                sorting: { 
                  sortModel: [{ field: 'action_date_time', sort: 'desc' }] 
                },
              }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              // IMPORTANT: Don't set paginationMode="server" - let DataGrid handle it client-side
              sx={{
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: 'action.hover',
                  cursor: 'pointer',
                },
                border: 'none',
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Loading Overlay */}
      {loading && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
    </Box>
  );
};

export default AdminAuditLog;